import React, {useEffect, useMemo, useState} from 'react';
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {useDispatch, useSelector} from "react-redux";
import orderBy from "lodash-es/orderBy";
import groupBy from 'lodash-es/groupBy';
import {
    sortAlphabetically
} from "@arboxappv4/shared/src/helpers/functions";
import {
    EllipsisText,
    FlexColumnSection, FlexRowSection,
    FlexSection, SeparatorVerticalDiv,
    SmallHeader,
    SmallText
} from "../../../styles/globalStyles";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {Spin} from "antd";
import {t} from "i18next";
import {
    addFiltersToQueryParams, getAttendanceDisplay,
    getCourseInfoArray, getMembershipAddons,
    getMembershipPriceText,
    sortCourseEventDays
} from "../../../helpers/functions";
import CustomCollapse from "../../../components/UI/CustomCollapse";
import dayjs from "dayjs";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import {updateFlags} from "@arboxappv4/shared/src/redux/stepperSlice";
import SingleItemPage from "../SingleItemPage";
import CoursesHeader from "./CoursesHeader";
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    COURSE_MEMBERSHIP_MODAL,
    openModal,
    SESSION_MEMBERSHIP_MODAL
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {DB_DATE_FORMAT, DB_TIME_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {getDisabledPagesCourseProps} from "@arboxappv4/shared/src/helpers/DisablePages";
import {membershipTypes} from "@arboxappv4/shared/src/helpers/constants";
import { courseBookingAllowed } from "../../../helpers/functions";


const Courses = (props) => {
    const { handleSubmit, values, setFieldValue, isDirectCourse } = props;
    const globalLocation = useSelector(state => state.site.selectedLocation)
    const loggedIn = useSelector(state => state.auth.loggedIn)
    const user = useSelector(state => state.user.data)
    const flags = useSelector(state => state.stepper.flags)
    const activeUsersBoxes = useSelector(state => state.user.activeUsersBoxes)


    const [courses, setCourses] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isEmpty, setIsEmpty] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [filteredCourses, setFilteredCourses] = useState(null);

    const [searchParams] = useSearchParams();
    const currencySymbol = useMemo(() => globalLocation?.currency_symbol, [globalLocation]);
    const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
    const {isMobile} = useScreenSize()
    const dispatch = useDispatch()
    const navigate = useNavigate();

    useEffect(() => {
        if(globalLocation) {
            if(params.allLocations === 'true') {
                setSelectedLocation('all')
            } else {
                setSelectedLocation(globalLocation?.id)
            }
        }
    }, [globalLocation]);

    useEffect(() => {
        if(selectedLocation) {
            // dispatch(updateFlags({serviceReady: false, courseInfo: null}))
            fetchCourses()
            if (selectedLocation !== 'all') addFiltersToQueryParams(navigate, {location: selectedLocation, allLocations: false})
            else addFiltersToQueryParams(navigate, {allLocations: true})
        }

    }, [selectedLocation, user]);

    useEffect(() => {
        if(values.serviceId && flags.serviceReady) {
            dispatch(updateFlags({serviceReady: false}))
            handleSubmit()
        }
    }, [values.serviceId, flags.serviceReady]);

    useEffect(() => {
        if(filteredCourses) {
            const empty = Object.values(filteredCourses).every((courses) => courses.length === 0)
            setIsEmpty(empty)
        }
    }, [filteredCourses]);

    const fetchCourses = async () => {
        setFetching(true)
        setFilteredCourses(null)
        const coursesRes = await apiAction(`getHugim`, 'post', {location_id: selectedLocation === 'all' ? null : selectedLocation, user_id: user ? user.id : null});
        handleCoursesResult(coursesRes)
        setFetching(false)
    }

    const handleCoursesResult = (coursesRes) => {
        setIsEmpty(coursesRes.length === 0)
        let sorted = sortAlphabetically(coursesRes, 'series_name');
        sorted = orderBy(sorted, ['start_date']);
        const groupedByType = groupBy(sorted, course => course.box_categories.name);
        setCourses(groupedByType)
        setFilteredCourses(groupedByType)
    }

    const getCourseHeaderInfo = (course) => {
        const isOneDayOfWeek = course.series_events.length === 1
        let days = sortCourseEventDays(course.series_events).map(day => t(`day${isOneDayOfWeek ? '' : '-short'}-${day.day_of_week}`))
        const displayProps =  getDisabledPagesCourseProps(course)
        const attendanceDisplay = getAttendanceDisplay(displayProps, course)

        return (
            <div style={{width: isMobile ? '100%' : 'auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '20px',  alignItems: isMobile ? 'start' : 'center'}}>
                <EllipsisText isMobile={isMobile} width={'150px'}>{getMembershipPriceText(course.membership_types[0], currencySymbol)}</EllipsisText>
                <EllipsisText isMobile={isMobile} width={'150px'}>{isOneDayOfWeek ? t('in-day', {day: days[0]}) : t('in-days', {days: days.join(', ')})}</EllipsisText>
                {((displayProps.showNumRegistered || displayProps.showNumOpenSpots) && !course.isHugFull) && <EllipsisText isMobile={isMobile} width={'80px'}>
                    <FlexRowSection gap={'8px'}>
                        <FontAwesomeIcon icon={solid('user')} color={Colors.siteBlack} size={'xs'}/>
                        <span>{attendanceDisplay}</span>
                    </FlexRowSection>
                </EllipsisText>}
                {selectedLocation === 'all' && <EllipsisText isMobile={isMobile} width={loggedIn ? '50%' : '100px'}>{course.locations_box.location}</EllipsisText>}
            </div>
        )
    }

    const getCollapseInfo = (course) => {
        const info = getCourseInfoArray(course)
        const days = sortCourseEventDays(course.series_events)
        const hasCustomFields = info.customFields?.props?.children?.length > 0;
        const isOneDayHug = course.end_date && dayjs(course.end_date, DB_DATE_FORMAT).diff(course.start_date, 'day') === 1 && days.length === 1
        const displayProps = getDisabledPagesCourseProps(course)

        return (
            <FlexColumnSection gap={isMobile ? '8px' : '15px'}>
                <FlexSection gap={isMobile ? '8px' : '15px'} flex={'unset'} mobile={isMobile} align={'start'}>
                    {(course.box_categories.bio || course.live_link || course.coach || hasCustomFields) &&
                    <FlexColumnSection>
                        {info.coach && displayProps.showCoach &&
                        <div>
                            <SmallText weight={'bold'}>{info.second_coach ? t('staff-members') : t('staff-member')}:</SmallText>
                            <FlexColumnSection gap={'0.5rem'}>
                                {info.coach}
                                {info.second_coach}
                            </FlexColumnSection>
                        </div>
                        }
                        { hasCustomFields && <div style={{ paddingTop: '10px' }}>{info.customFields}</div> }
                        {
                            course.box_categories.bio &&
                            <div style={{ paddingTop: hasCustomFields || (info.coach && displayProps.showCoach) ? '10px' : 0 }}>
                                <SmallText weight={'bold'}>{t('description')}:</SmallText>
                                <div style={{flex: 1, whiteSpace: 'pre-wrap'}}>{course.box_categories.bio}</div>
                            </div>
                        }
                        {course.live_link && <div style={{ paddingTop: '10px' }}>{t('online-course')}</div>}
                    </FlexColumnSection>}
                    {(course.box_categories.bio || course.live_link || course.coach || hasCustomFields) && <SeparatorVerticalDiv color={Colors.greyText} height={'100%'} margin={'0 10px'}/>}
                    <div style={{width: '320px'}}>
                        <FlexColumnSection flex={'unset'}>
                            <SmallText weight={'bold'}>{t('course-days-header')}</SmallText>
                            { course.start_date === course.end_date ?
                                <FlexColumnSection flex={'unset'}>
                                    <div>{t('one-day-hug', {start: dayjs(course.start_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})}</div>
                                </FlexColumnSection>
                                :
                                <FlexColumnSection flex={'unset'}>
                                    <div>{t('starts-at', {start: dayjs(course.start_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})}</div>
                                    {course.end_date && <div>{t('ends-at', {end: dayjs(course.end_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})}</div>}
                                </FlexColumnSection>
                            }
                            {days.map(day => <div key={`key-day-${day.day_of_week}`}>{t(`day-${day.day_of_week}`)}: {dayjs(day.start, DB_TIME_FORMAT).format(globalLocation.time_format)} - {dayjs(day.end, DB_TIME_FORMAT).format(globalLocation.time_format)}{day.description ? ` - ${day.description}` : ''}</div>)}
                            {(course.allow_mid_booking && course.has_started) ? <div>{t('hug-started-expand-label')}</div> : null}
                            {displayProps.showNumRemainingSlots && course.end_date && <div>{t('remaining-slots', {remainingSlots: course.remainingSlots})}</div>}
                        </FlexColumnSection>
                    </div>
                    <SeparatorVerticalDiv color={Colors.greyText} height={'100%'} margin={'0 10px'}/>
                    <FlexColumnSection>
                        <SmallText weight={'bold'}>{t('price')}:</SmallText>
                        <div>{getMembershipPriceText(course.membership_types[0], currencySymbol)} {course.membership_types[0].addons?.length || course.partialBookingOptions?.length ? t(`for-hug-${course.membership_types[0].type}`) : ''}</div>
                        {course?.partialBookingOptions && course.partialBookingOptions.map(option => <div>{t(`${option.numOfDays}-numOfDays`, {price: option.price, currencySymbol})}</div>) }
                        {course.membership_types[0].addons?.length > 0 && <div>{getMembershipAddons(course.membership_types[0].addons, currencySymbol)}</div>}
                        {course.membership_types[0].max_installments > 1 && <div>{t('max-installments',{max_installments: course.membership_types[0].max_installments})}</div>}
                        {course.register_group_member ? t('register-group-member-course-label') : null}
                    </FlexColumnSection>
                </FlexSection>
            </FlexColumnSection>
        )
    }

    const onCourseClick = (course, event = null) => {
        if(event) event.stopPropagation();
        setFieldValue('course', course) // being used only in direct page but there is always a validation, so we need to insert this to values
        dispatch(openModal({modalName: COURSE_MEMBERSHIP_MODAL, props: {
            course: course,
            onFullCourseSelected: (membershipType, trialSessionId = null) => onFullCourseSelected(membershipType, course, trialSessionId),
            onPartialBookingSelected: (membershipType, days) => onPartialBookingSelected(membershipType, days, course)
        }}))
    }

    const onFullCourseSelected = (membershipType, course, trialSessionId = null) => {
        if(membershipType) {
            setFieldValue('trialSessionId', membershipType.type === membershipTypes.TRIAL && trialSessionId ? trialSessionId : null)
            setFieldValue('selectedSeriesEvents', null)
            setFieldValue('serviceId', membershipType.id)
            setFieldValue('locations_box_fk', course.locations_box_fk)
            dispatch(updateFlags({serviceReady: true, courseInfo: course, isChildRegistration: !!course.register_group_member}))
        }
    }

    const onPartialBookingSelected = (membershipType, days, course) => {
        if(membershipType) {
            setFieldValue('trialSessionId', null)
            setFieldValue('selectedSeriesEvents', days)
            setFieldValue('serviceId', membershipType.id)
            setFieldValue('locations_box_fk', course.locations_box_fk)
            dispatch(updateFlags({serviceReady: true, courseInfo: course, isChildRegistration: !!course.register_group_member}))
        }
    }

    const onFilterChange = (filteredCourses, filtersConfig) => {
        if(filtersConfig?.locations) setSelectedLocation(filtersConfig?.locations.selected ?? null)
        setFilteredCourses(filteredCourses)
    }

    return (
        <>
            {isDirectCourse ?
                <SingleItemPage values={values} setFieldValue={setFieldValue} handleSubmit={handleSubmit} params={{...values, onCtaClick: () => onCourseClick(values.course)}}/>
                :
                <FlexColumnSection gap={'15px'} padding={isMobile ? '0 20px' : 0}>
                    <CoursesHeader courses={courses}
                                   onFiltered={onFilterChange}
                                   selectedLocation={selectedLocation}
                    />
                    <Spin spinning={fetching}/>
                    <FlexColumnSection gap={'20px'} overflow={'hidden auto'}>
                        {isEmpty && <span>{t('courses-empty-state')}</span>}
                        {filteredCourses && Object.keys(filteredCourses).map(coursesType => (
                            <React.Fragment key={`coursesType-${coursesType}`}>
                                {filteredCourses[coursesType].length > 0 &&
                                <FlexColumnSection gap={'5px'} overflow={'unset'} flex={'unset'}>
                                    <SmallHeader fontSize={'18px'}>{coursesType}</SmallHeader>
                                    <FlexColumnSection gap={'10px'} overflow={'unset'} flex={'unset'}>
                                        {filteredCourses[coursesType].map(course => (
                                            <CustomCollapse headerText={course.series_name}
                                                            headerFontSize={'16px'}
                                                            ctaText={t(course.isBookedUser ? 'user-booked-to-course' : course.isHugFull ? 'full-course' : 'book')}
                                                            headerWidth={'40%'}
                                                            headerInfo={getCourseHeaderInfo(course)}
                                                            onBtnClick={(event) => onCourseClick(course, event)}
                                                            key={`course-panel-${course.id}`}
                                                            restStyles={isMobile ? '' : 'width: 100px;'}
                                                            disableExpand={course.isHugFull || course.isBookedUser || !courseBookingAllowed(activeUsersBoxes, course) ? 'disabled' : null}
                                                            ctaWidth={course.isHugFull || course.isBookedUser ? 'auto' : '100px'}
                                            >
                                                {getCollapseInfo(course)}
                                            </CustomCollapse>
                                        ))}
                                    </FlexColumnSection>
                                </FlexColumnSection>}
                            </React.Fragment>
                        ))}
                    </FlexColumnSection>
                </FlexColumnSection>
            }
        </>
    );
};

export default Courses;
